import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, Flag, ShieldAlert } from 'lucide-react';
import { useFlaggedContent, useModerationReports, useOverviewBadges } from '../hooks/useAdminOps';
import {
  DetailDrawer,
  QueueTable,
  ReasonActionBar,
  StatusChip,
  type ChipTone,
  type QueueColumn,
  type ReasonOption,
  type SecondaryReasonField,
} from '../components/ui';
import type {
  FlaggedContentItem,
  ModerationQueueView,
  ModerationReportItem,
  QueueBadge,
  UserReportResolutionAction,
} from '../api/adminOps';
import {
  isOrganizationImageTarget,
  isOrganizationTarget,
  isUserReport,
  moderationActionsFor,
  ORGANIZATION_IMAGE_MISSING_TEXT,
  ORGANIZATION_PROFILE_MISSING_TEXT,
  organizationImageOf,
  organizationProfileOf,
  recordsViolationType,
  reportDrawerTitle,
  USER_REPORT_ACTIONS,
  userReportActionHint,
} from './moderation.logic';
import { enforcementHref } from './userDeletion.logic';
import OrganizationProfileDiff from '../components/OrganizationProfileDiff';

/** Wire enum for POST /admin/moderation/{itemId}/action. */
export type ModerationActionKind = 'APPROVE' | 'FLAG' | 'REMOVE' | 'WARN' | 'DISMISS';

/**
 * Author-safe violation categories accepted by the endpoint's violation_type
 * field. Mirrors ViolationCategory in @donatemate/content-moderation-models;
 * kept as a local wire enum for the same reason ModerationActionKind is.
 */
export type ViolationTypeKind =
  | 'spam'
  | 'harassment'
  | 'hate_speech'
  | 'sexual_content'
  | 'violence_or_threats'
  | 'community_guidelines';

export interface ModerationActionInput {
  itemId: string;
  targetType: string;
  targetId: string;
  action: ModerationActionKind;
  note?: string;
  violationType?: ViolationTypeKind;
}

/** DM-7283: input for POST /admin/moderation/users/{id}/resolution. */
export interface UserReportResolutionInput {
  userId: string;
  action: UserReportResolutionAction;
  reason: string;
  note?: string;
}

interface ModerationProps {
  /** Wired by the route to the moderationAction mutation hook. */
  onModerationAction?: (input: ModerationActionInput) => void;
  isActionPending?: boolean;
  /** DM-7283: wired by the route to the resolveUserReports mutation hook. */
  onUserReportResolution?: (input: UserReportResolutionInput) => void;
  isUserReportResolutionPending?: boolean;
}

/**
 * DM-6175: REMOVE writes the category onto the removal tombstone and names it
 * in the author's notification, so it is required rather than defaulted. The
 * other actions do not record a category.
 */
const VIOLATION_TYPES: ReasonOption[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Bullying or harassment' },
  { value: 'hate_speech', label: 'Hate speech' },
  { value: 'sexual_content', label: 'Nudity or sexual content' },
  { value: 'violence_or_threats', label: 'Violence or threats' },
  { value: 'community_guidelines', label: 'Community Guidelines (general)' },
];

const VIOLATION_TYPE_FIELD: SecondaryReasonField = {
  label: 'Violation category',
  placeholder: 'Select the category the author is told about…',
  options: VIOLATION_TYPES,
  requiredFor: ['REMOVE'],
};

type DrawerSelection =
  { view: 'reports'; item: ModerationReportItem } | { view: 'flagged'; item: FlaggedContentItem };

function moderationStatusTone(status: string): ChipTone {
  const normalized = status.toUpperCase();
  if (normalized === 'APPROVED') return 'ok';
  if (normalized === 'REMOVED' || normalized === 'REJECTED') return 'crit';
  if (normalized === 'FLAGGED' || normalized === 'REPORTED' || normalized === 'PENDING')
    return 'warn';
  return 'neutral';
}

function formatBadge(badge: QueueBadge | undefined): string {
  if (badge === undefined) return '';
  return badge.truncated ? ` (${badge.count}+)` : ` (${badge.count})`;
}

/**
 * Potentially harmful text stays blurred until the reviewer opts in —
 * moderators shouldn't be exposed to flagged content without intent.
 */
function RedactedExcerpt({ text }: { text: string | null }) {
  const [revealed, setRevealed] = useState(false);
  if (!text) return <span className="text-gray-text">—</span>;
  if (revealed) return <span className="whitespace-pre-wrap break-words">{text}</span>;
  return (
    <button
      onClick={event => {
        event.stopPropagation();
        setRevealed(true);
      }}
      className="text-left"
      title="Click to reveal"
      aria-label="Reveal hidden content"
    >
      <span className="blur-sm select-none">{text}</span>
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-text uppercase tracking-wide">{label}</p>
      <div className="text-sm text-white mt-0.5 break-words">{children}</div>
    </div>
  );
}

/**
 * DM-7976: a report or flag against a charity's organization profile is
 * judged on the proposed change, so the drawer shows the live profile beside
 * the draft. The approve/reject action bar below it is the ordinary one.
 */
function OrganizationProfile({ item }: { item: ModerationReportItem | FlaggedContentItem }) {
  const profile = organizationProfileOf(item);
  return (
    <Field label="Organization profile">
      {profile === null ? (
        <span className="text-gray-text">{ORGANIZATION_PROFILE_MISSING_TEXT}</span>
      ) : (
        <OrganizationProfileDiff live={profile.live} draft={profile.draft} />
      )}
    </Field>
  );
}

/**
 * DM-7976 (B1): an escalated charity logo or cover. The held image (a 60 s
 * link) beside the one the profile shows now, with the moderation reason.
 */
function OrganizationImage({ item }: { item: ModerationReportItem }) {
  const image = organizationImageOf(item);
  if (image === null) {
    return (
      <Field label="Organization image">
        <span className="text-gray-text">{ORGANIZATION_IMAGE_MISSING_TEXT}</span>
      </Field>
    );
  }
  return (
    <Field label={`Organization ${image.kind === 'LOGO' ? 'logo' : 'cover'}`}>
      <div className="grid grid-cols-2 gap-3 text-xs text-gray-text">
        <figure className="space-y-1">
          <figcaption>Now on the profile</figcaption>
          {image.live_url === null ? (
            <span>None</span>
          ) : (
            <img src={image.live_url} alt="Current" className="max-h-40 rounded" />
          )}
        </figure>
        <figure className="space-y-1">
          <figcaption>Held for review</figcaption>
          {image.view_url === null ? (
            <span>No longer held ({image.status})</span>
          ) : (
            <img src={image.view_url} alt="Held for review" className="max-h-40 rounded" />
          )}
        </figure>
      </div>
      {image.moderation_reason !== null && (
        <p className="mt-2 text-xs text-gray-text">Moderation: {image.moderation_reason}</p>
      )}
    </Field>
  );
}

export default function Moderation({
  onModerationAction,
  isActionPending,
  onUserReportResolution,
  isUserReportResolutionPending,
}: ModerationProps) {
  const [activeView, setActiveView] = useState<ModerationQueueView>('reports');
  const [selected, setSelected] = useState<DrawerSelection | null>(null);

  const overview = useOverviewBadges();
  const reports = useModerationReports();
  const flagged = useFlaggedContent();

  const queues = overview.data?.data.queues;

  const reportColumns: QueueColumn<ModerationReportItem>[] = [
    {
      key: 'target',
      header: 'Target',
      render: row => (
        <span>
          <span className="text-gray-text">{row.target_type}</span>{' '}
          <span className="font-mono text-xs">{row.target_id}</span>
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      render: row => <StatusChip label={row.reason} tone="warn" />,
    },
    {
      key: 'description',
      header: 'Description',
      render: row => (
        <span className="block max-w-xs truncate text-gray-text">{row.description ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: row => <StatusChip label={row.status} tone={moderationStatusTone(row.status)} />,
    },
    {
      key: 'created',
      header: 'Reported',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
      ),
    },
  ];

  const flaggedColumns: QueueColumn<FlaggedContentItem>[] = [
    {
      key: 'target',
      header: 'Content',
      render: row => (
        <span>
          <span className="text-gray-text">{row.target_type}</span>{' '}
          <span className="font-mono text-xs">{row.id}</span>
        </span>
      ),
    },
    {
      key: 'excerpt',
      header: 'Excerpt',
      render: row => (
        <div className="max-w-xs">
          <RedactedExcerpt text={row.content_excerpt} />
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: row => (
        <StatusChip
          label={row.moderation_status}
          tone={moderationStatusTone(row.moderation_status)}
        />
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      render: row =>
        row.moderation_severity ? (
          <StatusChip label={row.moderation_severity} tone="crit" />
        ) : (
          <span className="text-gray-text">—</span>
        ),
    },
    {
      key: 'reports',
      header: 'Reports',
      render: row => <span>{row.report_count}</span>,
    },
    {
      key: 'created',
      header: 'Created',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
      ),
    },
  ];

  const activeQueue = activeView === 'reports' ? reports : flagged;

  const drawerTitle =
    selected === null
      ? ''
      : selected.view === 'reports'
        ? reportDrawerTitle(selected.item)
        : `Flagged — ${selected.item.target_type}`;

  // DM-7283: a report against a USER has no item to act on; it resolves
  // through its own route (see the user-report action bar below).
  const selectedUserReport =
    selected !== null && selected.view === 'reports' && isUserReport(selected.item)
      ? selected.item
      : null;

  const actionTarget: Pick<ModerationActionInput, 'itemId' | 'targetType' | 'targetId'> | null =
    selected === null || selectedUserReport !== null
      ? null
      : selected.view === 'reports'
        ? {
            itemId: selected.item.id,
            targetType: selected.item.target_type,
            targetId: selected.item.target_id,
          }
        : {
            itemId: selected.item.id,
            targetType: selected.item.target_type,
            targetId: selected.item.id,
          };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Moderation</h1>
          <p className="text-sm text-gray-text">
            Review reported and auto-flagged content; every action is recorded in the audit ledger
          </p>
        </div>
      </div>

      {/* View tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            setActiveView('reports');
            setSelected(null);
          }}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            activeView === 'reports'
              ? 'bg-primary text-white'
              : 'bg-gray-800 text-gray-text hover:text-white'
          }`}
        >
          <Flag className="w-4 h-4" />
          <span>Reports{formatBadge(queues?.reports_pending)}</span>
        </button>
        <button
          onClick={() => {
            setActiveView('flagged');
            setSelected(null);
          }}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            activeView === 'flagged'
              ? 'bg-primary text-white'
              : 'bg-gray-800 text-gray-text hover:text-white'
          }`}
        >
          <ShieldAlert className="w-4 h-4" />
          <span>Flagged{formatBadge(queues?.flagged_content)}</span>
        </button>
      </div>

      {/* Queue */}
      {activeQueue.isError ? (
        <div className="glass-card p-6">
          <div className="flex items-center space-x-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load moderation queue: {activeQueue.error.message}</span>
          </div>
        </div>
      ) : activeView === 'reports' ? (
        <QueueTable
          columns={reportColumns}
          rows={reports.rows}
          rowKey={row => row.id}
          isLoading={reports.isPending}
          onRowClick={row => setSelected({ view: 'reports', item: row })}
          emptyTitle="No pending reports"
          emptyHint="User reports land here as soon as they are filed."
          hasNextPage={reports.hasNextPage}
          isFetchingNextPage={reports.isFetchingNextPage}
          onLoadMore={() => reports.fetchNextPage()}
        />
      ) : (
        <QueueTable
          columns={flaggedColumns}
          rows={flagged.rows}
          rowKey={row => row.id}
          isLoading={flagged.isPending}
          onRowClick={row => setSelected({ view: 'flagged', item: row })}
          emptyTitle="No flagged content"
          emptyHint="Content auto-flagged by moderation lands here."
          hasNextPage={flagged.hasNextPage}
          isFetchingNextPage={flagged.isFetchingNextPage}
          onLoadMore={() => flagged.fetchNextPage()}
        />
      )}

      {/* Detail drawer */}
      <DetailDrawer
        open={selected !== null}
        title={drawerTitle}
        onClose={() => setSelected(null)}
        footer={
          selectedUserReport !== null ? (
            onUserReportResolution ? (
              <ReasonActionBar
                key={selectedUserReport.id}
                idPrefix="user-report-resolution"
                reasons={USER_REPORT_ACTIONS}
                actionLabel="Resolve reports against user"
                destructive
                isPending={isUserReportResolutionPending}
                noteRequired
                notePlaceholder="Why, in your own words — kept with the decision and, for a removal, on the pending deletion"
                reasonHint={userReportActionHint}
                onSubmit={(action, note) => {
                  onUserReportResolution({
                    userId: selectedUserReport.target_id,
                    action: action as UserReportResolutionAction,
                    reason: note ?? action,
                    ...(note !== undefined ? { note } : {}),
                  });
                  setSelected(null);
                }}
              />
            ) : (
              <p className="text-sm text-gray-text">
                User-report resolution is not wired on this route yet.
              </p>
            )
          ) : (
            actionTarget !== null &&
            (onModerationAction ? (
              <ReasonActionBar
                key={actionTarget.itemId}
                reasons={moderationActionsFor({ target_type: actionTarget.targetType })}
                secondary={
                  recordsViolationType({ target_type: actionTarget.targetType })
                    ? VIOLATION_TYPE_FIELD
                    : undefined
                }
                actionLabel="Apply moderation action"
                isPending={isActionPending}
                notePlaceholder="Context for the author notification and audit trail"
                onSubmit={(action, note, violationType) => {
                  onModerationAction({
                    ...actionTarget,
                    action: action as ModerationActionKind,
                    note,
                    ...(violationType !== undefined
                      ? { violationType: violationType as ViolationTypeKind }
                      : {}),
                  });
                  setSelected(null);
                }}
              />
            ) : (
              <p className="text-sm text-gray-text">
                Moderation actions are not wired on this route yet.
              </p>
            ))
          )
        }
      >
        {selected?.view === 'reports' && (
          <>
            <Field label="Report ID">
              <span className="font-mono text-xs">{selected.item.id}</span>
            </Field>
            <Field label="Target">
              <span className="text-gray-text">{selected.item.target_type}</span>{' '}
              <span className="font-mono text-xs">{selected.item.target_id}</span>
              {selectedUserReport !== null && (
                <>
                  {' '}
                  <a href={enforcementHref(selectedUserReport.target_id)} className="text-primary">
                    open enforcement
                  </a>
                </>
              )}
            </Field>
            <Field label="Reason">
              <StatusChip label={selected.item.reason} tone="warn" />
            </Field>
            <Field label="Status">
              <StatusChip
                label={selected.item.status}
                tone={moderationStatusTone(selected.item.status)}
              />
            </Field>
            <Field label="Reporter description">
              {selected.item.description ?? <span className="text-gray-text">—</span>}
            </Field>
            <Field label="Reported">
              {formatDistanceToNow(new Date(selected.item.created_at), { addSuffix: true })}
            </Field>
            {isOrganizationTarget(selected.item) && <OrganizationProfile item={selected.item} />}
            {isOrganizationImageTarget(selected.item) && <OrganizationImage item={selected.item} />}
          </>
        )}
        {selected?.view === 'flagged' && (
          <>
            <Field label="Content ID">
              <span className="font-mono text-xs">{selected.item.id}</span>
            </Field>
            <Field label="Type">{selected.item.target_type}</Field>
            <Field label="Author">
              <span className="font-mono text-xs">{selected.item.author_user_id}</span>
            </Field>
            <Field label="Excerpt">
              <RedactedExcerpt text={selected.item.content_excerpt} />
            </Field>
            <Field label="Status">
              <StatusChip
                label={selected.item.moderation_status}
                tone={moderationStatusTone(selected.item.moderation_status)}
              />
            </Field>
            <Field label="Severity">
              {selected.item.moderation_severity ?? <span className="text-gray-text">—</span>}
            </Field>
            <Field label="Moderation note">
              {selected.item.moderation_note ?? <span className="text-gray-text">—</span>}
            </Field>
            <Field label="Report count">{selected.item.report_count}</Field>
            <Field label="Created">
              {formatDistanceToNow(new Date(selected.item.created_at), { addSuffix: true })}
            </Field>
            {isOrganizationTarget(selected.item) && <OrganizationProfile item={selected.item} />}
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
